// What a privileged Control Plane act is going to cost in approvals.
//
// §11 of the operator brief requires the review screen to state RISK CLASS and
// APPROVAL REQUIREMENT *before* a proposal is requested. §14 forbids the
// browser from computing an approval threshold as security authority. Both are
// right, and this module is the seam between them.
//
// THE SERVER IS THE AUTHORITY, ALWAYS. `classifyAdminActionRisk` is pure and
// server-side, the lane comes from the Worker's own `AUTH_V2_ISS`, and the
// threshold is written into the proposal row at creation. The console learns
// all three as FACTS the moment a proposal exists — `createProposal` returns
// `actionType`, `thresholdRequired` and `lane`.
//
// What this module supplies is the sentence an operator needs one step
// earlier, on the review screen, before they commit to asking. It is an
// EXPECTATION and is labelled as one everywhere it is rendered. Nothing gates
// on it: no button is enabled by it, no execute is permitted by it, and when
// the server's actual answer arrives, `reconcile` compares the two and the
// screen shows the server's — loudly, if they differ.
//
// The rule below mirrors `gao-id-worker` develop `5c340b5`:
//   `controlPlaneActionFor(op, lane)` in `src/lib/control-plane.approval.ts`
//   `classifyAdminActionRisk` in `src/lib/admin-owner-enforcement.ts`
//   `DEFAULT_THRESHOLD_BY_CLASS` / `VETO_CLASSES` in
//   `src/lib/admin-multisign-approval.ts`
//
// If that rule changes and this does not, `reconcile` is what says so — on the
// screen, at the moment it matters — instead of the console quietly stating a
// stale number forever.

import type { Lane } from "./contract";
import type { ControlPlaneOperation, ProposalActionClass, ProposalResponse } from "./api";

export interface GovernanceExpectation {
  /** The server-side action name this maps to. Shown so an operator reading
   *  an audit row can match it up. */
  actionName: string;
  riskClass: ProposalActionClass;
  /** Distinct owner approvals needed before the act can be executed. */
  approvalsRequired: number;
  /** True when one rejection alone kills the proposal. Neither Control Plane
   *  class is a veto class today; stated rather than assumed. */
  singleRejectVetoes: boolean;
}

/** `product.control_plane.<op>` on dev/test, `.product` on the Product lane.
 *  The lane is joined to the operation in exactly one place server-side, and
 *  in exactly one place here. */
export function expectedActionName(op: ControlPlaneOperation, lane: Lane): string {
  return lane === "production"
    ? `product.control_plane.${op}.product`
    : `product.control_plane.${op}`;
}

/**
 * What the server is expected to require.
 *
 * `null` when the lane is unknown — which is the honest answer, and the one
 * the rest of this console already gives in that state (`LaneChip` renders
 * UNKNOWN rather than defaulting to DEV). Naming a class here without knowing
 * the lane would mean naming the cheaper one, on the screen whose whole job is
 * to stop a Product act being mistaken for a dev one.
 */
export function expectedGovernance(
  op: ControlPlaneOperation,
  lane: Lane | null,
): GovernanceExpectation | null {
  if (lane === null) return null;
  const riskClass: ProposalActionClass =
    lane === "production" ? "high_config" : "low_config";
  return {
    actionName: expectedActionName(op, lane),
    riskClass,
    approvalsRequired: riskClass === "high_config" ? 2 : 1,
    singleRejectVetoes: false,
  };
}

export const RISK_CLASS_TEXT: Readonly<Record<ProposalActionClass, string>> = {
  low_config: "low_config — a configuration change on a non-Product lane",
  high_config: "high_config — a Product-grade configuration change",
  money_movement: "money_movement",
  production_deploy: "production_deploy",
};

/** One sentence naming what an operator has to collect. */
export function approvalRequirementText(g: GovernanceExpectation): string {
  return g.approvalsRequired === 1
    ? "one owner approval, on the Approvals screen, before it can be executed"
    : `${g.approvalsRequired} distinct owner approvals before it can be executed`;
}

export type GovernanceDrift =
  | { drifted: false }
  | { drifted: true; detail: string };

/**
 * Compare what was expected against what the server actually minted.
 *
 * A difference is never resolved in the console's favour: the returned
 * sentence tells the operator the server's answer is the real one and that the
 * console's stated expectation was wrong. That is the failure mode this
 * function exists for — a rule copied into a client and then changed on the
 * server is otherwise invisible until someone audits both.
 */
export function reconcile(
  expected: GovernanceExpectation | null,
  actual: ProposalResponse,
): GovernanceDrift {
  if (expected === null) return { drifted: false };
  const problems: string[] = [];
  if (expected.riskClass !== actual.actionType) {
    problems.push(
      `class ${actual.actionType}, this console expected ${expected.riskClass}`,
    );
  }
  if (expected.approvalsRequired !== actual.thresholdRequired) {
    problems.push(
      `threshold ${actual.thresholdRequired}, this console expected ${expected.approvalsRequired}`,
    );
  }
  if (problems.length === 0) return { drifted: false };
  return {
    drifted: true,
    detail:
      `The server minted this proposal with ${problems.join(" and ")}. ` +
      `The server's answer is the one that governs; this console's stated ` +
      `expectation is out of date and should be corrected in source.`,
  };
}
